import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges } from "@angular/core";
import { Comment } from "../models/comment";
import { MaCommentsPopupService } from "../ma-comments-popup/ma-comments-popup-service";
import { MessagesNewService } from "../messages-new/messages.service";
import { CommentDTO, MultiCommentStatusDTO } from "../ma-comments-popup/comment-dto";
import { MaPlatePreparationService } from "../ma-plate-preparation/ma-plate-preparation.service";
import { AnnouncedService } from "../apg-announced/services/announced.service";
import { SampleCommentDTO } from "../models/sampleCommentDTO";
import { MachineNewService } from "../machines-new/machines-new.service";
import { RunCommentDTO } from "../models-new/sampleCommentDTO";

@Component({
  selector: "app-comment",
  templateUrl: "./comment.component.html",
  styleUrls: ["./comment.component.css"],
})
export class CommentComponent implements OnChanges {
  @Input() plateID!: string;
  @Input() plateStatus!: string;
  @Input() showPlateComment = false;

  @Input() sampleID!: string;
  @Input() sampleBarcode!: string;
  @Input() showSampleComment = false;

  @Input() selectedLabID!: string;
  @Input() puid!: string;

  @Input() displayDialog = false;

  @Input() displayMultiCommentDialog = false;
  @Input() displayMultiPlateComment = false;
  @Input() multiPlateIdList: Array<string> = []

  @Input() samplePlateComments: Comment[] = [];
  @Input() sampleSearch = false;

  @Input() runId!: string;
  @Input() showRunComment = false;
  @Input() runStatus!: string;

  @Output() onCommentClosed = new EventEmitter<boolean>();

  header: string;
  comments: Comment[] = [];
  newComment: string = "";
  isLoading: boolean = false;
  plateOptions =[];
  selectedPlates = [];

  readonly COMMENT_ERROR_MESSAGE = "Failed to add a comment!";

  constructor(
    private commentService: MaCommentsPopupService,
    private messageService: MessagesNewService,
    private announcementService: AnnouncedService,
    private readonly platePreparationService: MaPlatePreparationService,
    private readonly machineNewService: MachineNewService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.displayDialog && this.displayDialog) {
      this.loadOptionsForMultiPlate();
      this.fetchComments();
    }
  }

  loadOptionsForMultiPlate(){
    if(this.displayMultiPlateComment){
      this.plateOptions = [];
      this.multiPlateIdList.forEach(plateID=>{
        this.plateOptions.push({ 'label': plateID, 'value': plateID })
      })
      
    }
  }

  fetchComments(): void {
    this.newComment = "";
    if (this.showPlateComment) {
      this.fetchPlateComment();
    } else if (this.showSampleComment) {
      this.fetchSampleComment();
    } else if (this.showRunComment) {
      this.fetchRunComment();
    }
  }

  fetchPlateComment(): void {
    if (this.sampleSearch) {
      this.comments = this.samplePlateComments;
    } else {
      this.isLoading = true;
      this.commentService.commentDetailsbyPlateId(this.selectedLabID, this.plateID).subscribe({
        next: (plateWithComments) => {
          this.isLoading = false;
          this.comments = plateWithComments.comments;
        },
        error: (_error) => {
          this.isLoading = false;
          this.messageService.addErrorMessage({
            severity: "error",
            summary: "Failed to fetch comments!",
            detail: `Failed to fetch the comments for the batch: ${this.plateID}`,
          });
          this.onCommentClosed.emit();
        }
      });
    }
  }

  fetchSampleComment(): void {
    this.newComment = "";
    this.isLoading = true;
    this.announcementService.getSampleComments(this.selectedLabID, this.sampleID).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.comments = result.data;
      },
      error: (_error) => {
        this.isLoading = false;
        this.messageService.addErrorMessage({
          severity: "error",
          summary: "Failed to fetch sample comments!",
        });
        this.onCommentClosed.emit();
      }
    });
  }

  fetchRunComment(): void {
    this.newComment = "";
    this.isLoading = true;
    this.machineNewService.getRunComments(this.selectedLabID, this.runId).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.comments = result.data;
      },
      error: (_error) => {
        this.isLoading = false;
        this.messageService.addErrorMessage({
          severity: "error",
          summary: "Failed to fetch run comments!",
        });
        this.onCommentClosed.emit();
      }
    });
  }

  postComment(): void {
    if(this.displayMultiCommentDialog || this.displayMultiPlateComment){
      this.saveMultiComment();
    }
    else if(this.showPlateComment) {
      this.addCommentToPlate();
    } else if(this.showRunComment) {
      this.addCommentToRun();
    } else {
      this.addCommentToSample();
    }
  }

  addCommentToPlate(): void {
    const commentDTO = new CommentDTO();
    commentDTO.platecomment = true;
    commentDTO.labid = this.selectedLabID;
    commentDTO.comment = this.newComment;
    commentDTO.plateid = this.plateID;
    commentDTO.puid = this.puid;
    commentDTO.status = this.plateStatus;
    this.newComment = "";
    this.addCurrentCommentToPlateComment(commentDTO);
    this.isLoading = true;
    this.platePreparationService.updateComment(commentDTO).subscribe({
      next: (result) => {
        this.isLoading = false;
        this.messageService.addSuccessMessage({
          severity: "success",
          summary: "Comment added!",
          detail: result,
        });
        this.fetchPlateComment();
      },
      error: (_error) => {
        this.isLoading = false;
        this.messageService.addErrorMessage({
          severity: "error",
          summary: this.COMMENT_ERROR_MESSAGE,
          detail: `Failed to publish a comment for the batch: ${this.plateID}`,
        });
      }
    });
  }

  addCommentToRun(): void {
    const runCommentDTO = new RunCommentDTO();
    runCommentDTO.puid = this.puid;
    runCommentDTO.runId = this.runId;
    runCommentDTO.status = this.runStatus;
    runCommentDTO.comment = this.newComment;

    this.isLoading = true;
    this.machineNewService.updateRunComment(this.selectedLabID, this.runId, runCommentDTO).subscribe({
      next: (_result) => {
        this.isLoading = false;
        this.messageService.addSuccessMessage({
          severity: "success",
          summary: "Sample comment added!",
        });
        this.fetchRunComment();
      },
      error: (_error) => {
        this.isLoading = false;
        this.messageService.addErrorMessage({
          severity: "error",
          summary: this.COMMENT_ERROR_MESSAGE,
          detail: `Failed to publish a comment for the sample`,
        });
      },
    });
  }

  addCurrentCommentToPlateComment(commentDTO : CommentDTO){
    const newComment = new Comment();
    newComment.comment = commentDTO.comment;
    newComment.plateId = commentDTO.plateid;
    newComment.puid = commentDTO.puid;
    newComment.status = commentDTO.status;
    newComment.timeStamp = new Date();
    this.samplePlateComments.push(newComment);
  }

  addCommentToSample(): void {
    const sampleComment = new SampleCommentDTO();
    sampleComment.puid = this.puid;
    sampleComment.status = "ADMIN_CHANGE";
    sampleComment.comment = this.newComment;

    this.isLoading = true;
    this.announcementService.updateSampleComment(this.selectedLabID, this.sampleID, sampleComment).subscribe({
      next: (_result) => {
        this.isLoading = false;
        this.messageService.addSuccessMessage({
          severity: "success",
          summary: "Sample comment added!",
        });
        this.fetchSampleComment();
      },
      error: (_error) => {
        this.isLoading = false;
        this.messageService.addErrorMessage({
          severity: "error",
          summary: this.COMMENT_ERROR_MESSAGE,
          detail: `Failed to publish a comment for the sample`,
        });
      },
    });
  }

  getMultiPlateCommentDTO() {
    let multiComment = new MultiCommentStatusDTO();
    multiComment.plateids = this.displayMultiPlateComment? this.selectedPlates : this.multiPlateIdList;
    multiComment.status = this.plateStatus;
    multiComment.comment = this.newComment;
    multiComment.labid = this.selectedLabID;
    multiComment.platecomment = true;
    multiComment.puid = this.puid;
    multiComment.beadchipstatus = "";
    multiComment.beadchipids = [];
    return multiComment;
  }

  saveMultiComment() {
    if(this.displayMultiPlateComment && this.selectedPlates.length === 0){
      this.messageService.addErrorMessage({ severity: 'warn', summary: 'Warning', detail: "Please select atleast one Plate to comment!"})
      return;
    }
    this.commentService.updateMultiComment(this.getMultiPlateCommentDTO()).subscribe(
      (result) => {
        this.newComment = "";
        this.messageService.addSuccessMessage({ severity: 'success', summary: 'Success', detail: result })
        if(this.displayMultiPlateComment){
          this.plateID = this.selectedPlates[0];
          this.fetchPlateComment();
        }
      },
      (error) => {
        this.messageService.addErrorMessage({ severity: 'error', summary: 'Error', detail: error.error })
      }
    )
  }

  onPlateSelect(event : any){
    if(!event.originalEvent.selected){
      this.plateID = event.itemValue.value;
      this.fetchPlateComment();
    }
  }
}
